import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { AiModelsComponent } from '../../../../../shared/ai-models/ai-models.component';
import { InstructionBuilderService } from '../services/instruction-builder.service';
import { IOpenAiModel } from '../../../../../interfaces/i-open-ai-model';
import { UtilitiesService } from '../../../../../services/utilities.service';

@Component({
  selector: 'app-instructions-ai-config',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSliderModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatSlideToggleModule,
    InputsModule,
    KENDO_BUTTONS,
    AiModelsComponent
],
  templateUrl: './instructions-ai-config.component.html',
  styleUrl: './instructions-ai-config.component.scss'
})
export class InstructionsAiConfigComponent {
  instructionForm!: FormGroup;

  #instructionBuilderService = inject(InstructionBuilderService);
  #utilitiesService = inject(UtilitiesService);

  constructor(private fb: FormBuilder) {

    effect(() => {
      this.instructionForm = this.fb.group({
        model: new FormControl<IOpenAiModel>(this.#instructionBuilderService.instruction().details.model),
        temperature: new FormControl(this.#instructionBuilderService.instruction().details.temperature, [Validators.required, Validators.min(0), Validators.max(1)]),
        topP: new FormControl(this.#instructionBuilderService.instruction().details.topP, [Validators.required, Validators.min(0), Validators.max(1)]),
      });
    });
  }

  modelSelected(event: any) {
    const modelFormControl = this.instructionForm.get('model');
    modelFormControl!.setValue(event);

    // Check if the model is GPT-5 and update temperature and topP to recommended values
    if (event?.model?.toLowerCase().includes('gpt-5')) {
      this.handleTemperatureChange(this.getRecommendedTemperature());
      this.handleTopPChange(this.getRecommendedTopP());
    }
  }

  handleTemperatureChange(event: number): void {
    let temperatureFormControl = this.instructionForm.get('temperature');
    temperatureFormControl!.setValue(event);
  }

  handleTopPChange(event: number): void {
    let topPFormControl = this.instructionForm.get('topP');
    topPFormControl!.setValue(event);
  }


  /**
   * Checks if the currently selected model is GPT-5
   * @returns boolean indicating if the selected model is GPT-5
   */
  isGpt5Model(): boolean {
    const model = this.instructionForm.get('model')?.value;
    return this.#utilitiesService.isGpt5Model(model?.model);
  }

  /**
   * Gets recommended temperature value for the current model
   * @returns number representing the recommended temperature
   */
  getRecommendedTemperature(): number {
    const model = this.instructionForm.get('model')?.value;
    return this.#utilitiesService.getRecommendedTemperature(model?.model);
  }

  /**
   * Gets recommended topP value for the current model
   * @returns number representing the recommended topP
   */
  getRecommendedTopP(): number {
    const model = this.instructionForm.get('model')?.value;
    return this.#utilitiesService.getRecommendedTopP(model?.model);
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.instructionForm) {
      return false;
    }

    this.instructionForm.markAllAsTouched();
    return this.instructionForm.valid;
  }
}
